import { useState } from "react";
import { Button } from "components/elements";
import { useCurrentUser } from "hooks/useCurrentUser";
import type { PermissionEntry } from "./ChartPermissionPicker";

interface UseExternalOrgConfirmOpts {
    existingEntries: PermissionEntry[];
}

function entryExternalOrgId(
    entry: PermissionEntry,
    ownOrgId: number | null | undefined,
): number | null {
    if (entry.target === "COMPASS_TEAM" || entry.target === "PUBLIC") return null;
    if (entry.orgId == null || ownOrgId == null) return null;
    return entry.orgId === ownOrgId ? null : entry.orgId;
}

/**
 * Adds a confirmation step when a chart permission would expand sharing
 * to a second distinct external organization. Own-org and one external
 * org are allowed silently per PRTL-1279.
 */
export function useExternalOrgConfirm({ existingEntries }: UseExternalOrgConfirmOpts) {
    const currentUser = useCurrentUser();
    const ownOrgId = currentUser?.organizationId;
    const [pendingEntry, setPendingEntry] = useState<{
        entry: PermissionEntry;
        resolve: (confirmed: boolean) => void;
    } | null>(null);

    const existingExternalOrgIds = new Set(
        existingEntries
            .map((e) => entryExternalOrgId(e, ownOrgId))
            .filter((id): id is number => id !== null),
    );

    const onBeforeAdd = (entry: PermissionEntry): Promise<boolean> => {
        const newExternalOrgId = entryExternalOrgId(entry, ownOrgId);
        if (newExternalOrgId === null) return Promise.resolve(true);
        // Adding more permissions to an org we already share with → no warning.
        if (existingExternalOrgIds.has(newExternalOrgId)) return Promise.resolve(true);
        // Own-org + 1 external is allowed silently; the 2nd+ external prompts.
        if (existingExternalOrgIds.size < 1) return Promise.resolve(true);
        return new Promise((resolve) => setPendingEntry({ entry, resolve }));
    };

    const handleConfirm = () => {
        pendingEntry?.resolve(true);
        setPendingEntry(null);
    };

    const handleCancel = () => {
        pendingEntry?.resolve(false);
        setPendingEntry(null);
    };

    const modal = pendingEntry ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
            <div className="w-[400px] max-w-[90vw] rounded-xl bg-white p-6 shadow-xl">
                <h3 className="mb-2 text-base font-semibold text-gray-900">
                    Share outside your organization?
                </h3>
                <p className="mb-6 text-sm text-gray-600">
                    This user is outside the current organization. Are you sure you want to share?
                </p>
                <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm}>Proceed</Button>
                </div>
            </div>
        </div>
    ) : null;

    return { onBeforeAdd, modal };
}
